'use client';

import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { FRAUD_CONFIG } from '../../constants/interaction/constants';
import type { InteractionTimingAdapter } from '../../model/interaction/adapters';

// =============================================================================
// Types
// =============================================================================

export type FraudType = 'rapid_scroll' | 'direction_change';

export type FraudLockStatus = 'unlocked' | 'locked';

export interface FraudLockState {
  status: FraudLockStatus;
  lockType: FraudType | null;
  lastWarningTime: number;
}

export type FraudLockAction =
  | { type: 'LOCK'; fraudType: FraudType; now: number }
  | { type: 'UNLOCK' };

// =============================================================================
// Reducer (Pure Function - Testable)
// =============================================================================

const INITIAL_STATE: FraudLockState = {
  status: 'unlocked',
  lockType: null,
  lastWarningTime: 0,
};

function fraudLockReducer(state: FraudLockState, action: FraudLockAction): FraudLockState {
  switch (action.type) {
    case 'LOCK': {
      // Already locked - don't re-lock
      if (state.status === 'locked') {
        return state;
      }
      // Cooldown check - ignore if within cooldown period
      if (
        state.lastWarningTime !== 0 &&
        action.now - state.lastWarningTime < FRAUD_CONFIG.cooldownMs
      ) {
        return state;
      }
      return {
        status: 'locked',
        lockType: action.fraudType,
        lastWarningTime: action.now,
      };
    }

    case 'UNLOCK': {
      if (state.status !== 'locked') {
        return state;
      }
      return {
        ...state,
        status: 'unlocked',
        lockType: null,
      };
    }

    default:
      return state;
  }
}

// =============================================================================
// Hook
// =============================================================================

export interface UseFraudLockStateMachineOptions {
  timing: InteractionTimingAdapter;
  /** Called when lock state changes to 'locked' */
  onLock?: () => void;
  /** Called when lock state changes to 'unlocked' */
  onUnlock?: () => void;
}

export interface UseFraudLockStateMachineReturn {
  status: FraudLockStatus;
  isLocked: boolean;
  lockType: FraudType | null;
  /** Attempt to lock. Returns true if lock was acquired. */
  lock: (fraudType: FraudType) => boolean;
  /** Schedule unlock after delay */
  scheduleUnlock: (delayMs: number) => void;
}

export function useFraudLockStateMachine({
  timing,
  onLock,
  onUnlock,
}: UseFraudLockStateMachineOptions): UseFraudLockStateMachineReturn {

  const [state, dispatch] = useReducer(fraudLockReducer, INITIAL_STATE);
  const unlockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onLockRef = useRef(onLock);
  onLockRef.current = onLock;
  const onUnlockRef = useRef(onUnlock);
  onUnlockRef.current = onUnlock;

  const prevStatusRef = useRef(state.status);

  // Effect to call callbacks on state change
  useEffect(() => {
    if (prevStatusRef.current !== state.status) {
      if (state.status === 'locked') {
        onLockRef.current?.();
      } else if (state.status === 'unlocked' && prevStatusRef.current === 'locked') {
        onUnlockRef.current?.();
      }
      prevStatusRef.current = state.status;
    }
  }, [state.status]);

  const clearUnlockTimer = useCallback(() => {
    if (unlockTimerRef.current) {
      clearTimeout(unlockTimerRef.current);
      unlockTimerRef.current = null;
    }
  }, []);

  const statusRef = useRef(state.status);
  statusRef.current = state.status;

  const lock = useCallback(
    (fraudType: FraudType): boolean => {
      if (statusRef.current === 'locked') {
        return false;
      }
      const now = timing.now();
      dispatch({ type: 'LOCK', fraudType, now });
      return true;
    },
    [timing],
  );

  const scheduleUnlock = useCallback(
    (delayMs: number) => {
      clearUnlockTimer();
      unlockTimerRef.current = setTimeout(() => {
        unlockTimerRef.current = null;
        dispatch({ type: 'UNLOCK' });
      }, delayMs);
    },
    [clearUnlockTimer],
  );

  // Cleanup on unmount only
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    return () => {
      if (unlockTimerRef.current) {
        clearTimeout(unlockTimerRef.current);
        unlockTimerRef.current = null;
      }
      if (stateRef.current.status === 'locked') {
        dispatch({ type: 'UNLOCK' });
      }
    };
  }, []);

  return useMemo(
    () => ({
      status: state.status,
      isLocked: state.status === 'locked',
      lockType: state.lockType,
      lock,
      scheduleUnlock,
    }),
    [state.status, state.lockType, lock, scheduleUnlock],
  );
}
